import { useActionState, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as emailValidator from 'email-validator';

import { ResetPasswordForm } from './ResetPasswordForm';
import { PlutoClientException } from '@/PlutoClientException';
import { sendReset } from '@/generated/client/passwordreset/passwordreset';
import { State } from '@/types';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { createInitState } from '@/utilities/formUtils';
import { useNotificationStore } from '@/stores/notificationstore';
import axios from 'axios';
import { getInvitationToken } from './utils/invitationToken';

export type ResetPasswordFields = {
  email: string;
  username: string;
};

export default function ResetPasswordPage({
  returnLoginPage,
  invitedUser,
}: {
  returnLoginPage: () => void;
  invitedUser: string | undefined;
}) {
  const resetPasswordState = createInitState<ResetPasswordFields>(['email', 'username']);
  const [state, formAction] = useActionState(tryResetPassword, resetPasswordState);
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen);
  const showNotification = useNotificationStore((state) => state.showNotification);

  const { i18n, t } = useTranslation();
  const lang = i18n.language;

  useEffect(() => {
    function handleResize() {
      setPhone(isPhoneOrHighScreen);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  async function tryResetPassword(
    prev: State<ResetPasswordFields>,
    formData: FormData
  ): Promise<State<ResetPasswordFields>> {
    const formPayload = Object.fromEntries(formData) as ResetPasswordFields;
    const missingUsername = !invitedUser && !formPayload.username;
    const missingEmail = !invitedUser && !formPayload.email;

    if (missingUsername || missingEmail) {
      return {
        ...prev,
        errors: {
          username: missingUsername ? t('login.error.missingUsername') : undefined,
          email: missingEmail ? t('login.error.missingEmail') : undefined,
        },
        globalError: undefined,
        success: false,
      };
    }

    if (!invitedUser ? !emailValidator.validate(formPayload.email) : !emailValidator.validate(invitedUser)) {
      return {
        ...prev,
        errors: {
          username: undefined,
          email: t('error.common.badEmail'),
        },
        globalError: undefined,
        success: false,
      };
    }

    try {
      const request = {
        desiredUser: !invitedUser ? formPayload.username : invitedUser,
        desiredEmail: !invitedUser ? formPayload.email : invitedUser,
        lang,
      };
      const invitationToken = getInvitationToken();
      const options = invitationToken ? { headers: { 'X-Invitation-Token': invitationToken } } : undefined;

      await sendReset(request, options);
      showNotification('success', t('login.resetPassword.successfulPasswordReset'), undefined, 5);
      return {
        globalError: undefined,
        errors: { username: undefined, email: undefined },
        success: true,
      };
    } catch (error) {
      if (!(error instanceof PlutoClientException)) {
        const errorCode = axios.isAxiosError(error) ? error.response?.status : undefined;

        if (errorCode === 403) {
          showNotification('error', t('login.resetPassword.invalidInvitationLink'), undefined, 5);
          return resetPasswordState;
        }

        showNotification('error', t('error.api.forgetpassword'), undefined, 5);
        return {
          globalError: t('error.api.forgetpassword'),
          errors: { username: undefined, email: undefined },
          success: false,
        };
      }
      return resetPasswordState;
    }
  }

  return (
    <ResetPasswordForm
      state={state}
      formAction={formAction}
      returnLoginPage={returnLoginPage}
      isPhone={isPhone}
      invitedUser={invitedUser}
    />
  );
}
